/**
 * Sapphire cart (1.6.1): the multi-product cart that Sapphire pages
 * (ordernutra.com) build their product grid, side cart and order summary
 * from. Ported from demo-engine 0.5.13 (utils.js: sapphireCart,
 * sapphireCartOutput, sapphireDisplayProducts) with the same names, the same
 * sessionStorage key ("sapphireCart") and the same markup contract, so a
 * Sapphire page moves to konnektive-one with only config and script-tag
 * changes.
 *
 * Inert unless used: nothing here runs at load. render() calls the two
 * renderers only in Sapphire mode (gateway: 'sapphire'); on any other page
 * they run only if the page calls them, and they do nothing without the
 * Sapphire template markup.
 *
 * Fixed while porting (demo-engine bugs): the cart rows no longer start with
 * the text "undefined"; each template on the page (side cart and checkout
 * summary) is drawn from its own markup instead of all from the first one; a
 * [sapphireCartItem="removeItemButton"] removes its own line (demo bound it
 * to a copy that was then thrown away); unreadable storage no longer throws.
 */
import $ from 'jquery';
import { getConfig } from './config';

const STORAGE_KEY = 'sapphireCart';

const emptyState = () => [{ items: [], itemsShort: [], coupon: [] }];

export const sapphireCart = {
	cart: emptyState(),

	// Same signature as demo-engine: the 10th argument is the image name, the
	// 11th the description. Adding a product already in the cart adds to its
	// quantity, so the cart holds one line per product.
	addItem(
		itemName,
		itemPrice,
		itemPriceShip,
		itemQuantity,
		itemID,
		itemCampaignID,
		itemShippingID,
		itemOfferID,
		itemBillingID,
		itemImageName,
		itemDescription,
	) {
		const existing = this.getItemByID(itemID);
		if (existing) {
			this.updateQuantity(itemID, parseInt(existing.itemQuantity, 10) + Math.abs(itemQuantity));
		} else {
			this.cart[0].items.push({
				itemName,
				itemPrice,
				itemPriceShip,
				itemQuantity: Math.abs(itemQuantity),
				itemID,
				itemCampaignID,
				itemShippingID,
				itemOfferID,
				itemBillingID,
				itemImageName: itemImageName || '',
				itemDescription,
			});
			this.cart[0].itemsShort.push({ itemQuantity: Math.abs(itemQuantity), itemID });
		}
		this.saveCart();
	},

	addCoupon(couponCode, couponType, couponDiscountValue, couponApplyTo, couponProductId) {
		this.removeCoupon();
		this.cart[0].coupon.push({
			couponCode,
			couponType,
			couponDiscountValue,
			couponApplyTo,
			couponProductId,
		});
		this.saveCart();
	},

	addCouponSimple(couponCode, couponDiscountValue) {
		this.addCoupon(couponCode, 'flatdiscount', couponDiscountValue, '', '');
	},

	removeCoupon() {
		this.cart[0].coupon = [];
		this.saveCart();
	},

	// Never below 1: removing a line is removeItem().
	updateQuantity(itemID, quantity) {
		const item = this.getItemByID(itemID);
		if (!item) return;
		let qty = quantity;
		if (qty <= 0) qty = 1;
		item.itemQuantity = Math.abs(qty);
		this.cart[0].items[item.index] = item;
		const short = this.cart[0].itemsShort[item.index];
		if (short) short.itemQuantity = item.itemQuantity;
		this.saveCart();
	},

	removeItem(itemID) {
		// eslint-disable-next-line eqeqeq
		this.cart[0].items = this.cart[0].items.filter((item) => item.itemID != itemID);
		// eslint-disable-next-line eqeqeq
		this.cart[0].itemsShort = this.cart[0].itemsShort.filter((item) => item.itemID != itemID);
		this.saveCart();
	},

	// Demo-engine shape: an array with cart / cartShort / coupon properties.
	getCart() {
		const out = [];
		out.cart = this.cart[0].items;
		out.cartShort = this.cart[0].itemsShort;
		out.coupon = this.cart[0].coupon;
		return out;
	},

	emptyCart() {
		this.cart = emptyState();
		this.saveCart();
	},

	saveCart() {
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.cart));
			this.unsaved = false;
		} catch (e) {
			// Storage blocked or full: the cart still works for this page, and
			// loadCart() must not replace it with the older stored copy.
			this.unsaved = true;
		}
	},

	saveCartToString() {
		return JSON.stringify(this.cart[0].itemsShort);
	},

	// Nothing saved means an empty cart (it was emptied, e.g. after an
	// order). Storage that cannot be read keeps the cart this page holds.
	loadCart() {
		// The last change on this page could not be saved: storage is older
		// than what the shopper sees, so keep this page's cart.
		if (this.unsaved) return;
		let saved = null;
		try {
			const raw = sessionStorage.getItem(STORAGE_KEY);
			if (raw === null) {
				this.cart = emptyState();
				return;
			}
			saved = JSON.parse(raw);
		} catch (e) {
			saved = null;
		}
		if (Array.isArray(saved) && saved[0] && Array.isArray(saved[0].items)) {
			saved[0].itemsShort = saved[0].itemsShort || [];
			saved[0].coupon = saved[0].coupon || [];
			this.cart = saved;
		}
	},

	getCount() {
		return this.cart[0].items.length;
	},

	getDiscount() {
		const coupon = this.cart[0].coupon[0];
		if (!coupon) return 0;
		let base;
		if (coupon.couponProductId) {
			const item = this.getItemByID(coupon.couponProductId);
			if (!item) return 0;
			// Every unit of the line, like getSubTotal()/getTotalShipping() (demo-engine took one unit).
			base = (coupon.couponApplyTo === 'SHIPPING' ? item.itemPriceShip : item.itemPrice) * item.itemQuantity;
		} else {
			base = coupon.couponApplyTo === 'SHIPPING' ? this.getTotalShipping() : this.getSubTotal();
		}
		if (coupon.couponType === 'flatdiscount') {
			return parseFloat(coupon.couponDiscountValue).toFixed(2);
		}
		if (coupon.couponType === 'percentage') {
			return ((base * coupon.couponDiscountValue) / 100).toFixed(2);
		}
		return 0;
	},

	getSubTotal() {
		return this.cart[0].items.reduce((sum, item) => sum + item.itemPrice * item.itemQuantity, 0);
	},

	getTotalShipping() {
		return this.cart[0].items
			.reduce((sum, item) => sum + item.itemPriceShip * item.itemQuantity, 0);
	},

	getTotal() {
		return (this.getSubTotal() + this.getTotalShipping() - this.getDiscount()).toFixed(2);
	},

	checkTrialProduct() {
		return this.cart[0].items.some((item) => item.itemIsTrial);
	},

	getItemByID(itemID) {
		const { items } = this.cart[0];
		for (let i = 0; i < items.length; i += 1) {
			// eslint-disable-next-line eqeqeq
			if (items[i].itemID == itemID) {
				items[i].index = i;
				return items[i];
			}
		}
		return undefined;
	},
};

// The cart the checkout uses. A page that still defines its own
// sapphireCart (a site cart.js) keeps it, as long as it can load and list
// its lines; otherwise the library's.
export function getSapphireCart() {
	const own = typeof window !== 'undefined' ? window.sapphireCart : null;
	if (own && typeof own.loadCart === 'function' && typeof own.getCart === 'function') {
		return own;
	}
	return sapphireCart;
}

function money(value) {
	return `$${parseFloat(value || 0).toFixed(2)}`;
}

function imageSrc(id, imageName) {
	const cfg = getConfig();
	if (cfg.imagesID) {
		return `${cfg.imagesPath}/product-${id}.${cfg.imagesExtension}`;
	}
	return `${cfg.imagesPath}/${imageName}.${cfg.imagesExtension}`;
}

// The templates are taken off the page the first time they are drawn (as
// demo-engine did at load) and kept, because drawing replaces them. Each
// leaves a comment marker in its place; a redraw swaps only the rows drawn
// after that marker, so anything else in the same parent stays put.
let cartTemplates = null;
let productTemplates = null;

// Tests only: forget the saved templates.
export function resetSapphireTemplates() {
	cartTemplates = null;
	productTemplates = null;
}

function takeTemplates(selector) {
	const found = [];
	$(selector).each(function eachTemplate() {
		const marker = document.createComment('sapphire template');
		const $tpl = $(this).clone();
		$(this).replaceWith(marker);
		found.push({ $tpl, $marker: $(marker), $drawn: $() });
	});
	return found;
}

function drawRows(template, rows) {
	template.$drawn.remove();
	template.$drawn = $(rows.map(($row) => $row[0]));
	template.$marker.after(template.$drawn);
}

/**
 * Draws the cart into every [data-rel="sapphireCartItemTemplate"] on the
 * page (one row per line) and fills [data-rel="sapphireCartCount"] and
 * [data-rel="sapphireCartTotal"]. Inside a template: [sapphireCartItem=
 * "name" | "price" | "priceSubtotal" | "quantity" | "image" |
 * "removeItemButton"]. Each row carries sapphireCartItem="cartEntry" and
 * sapphireEntryID="<product id>"; a quantity field is named "number-<id>".
 */
export function sapphireCartOutput() {
	const cart = getSapphireCart();
	const lines = cart.getCart().cart || [];

	$('[data-rel="sapphireCartCount"]').html(cart.getCount());
	$('[data-rel="sapphireCartTotal"]').html(`$${cart.getTotal()}`);

	if (!cartTemplates) {
		cartTemplates = takeTemplates('[data-rel="sapphireCartItemTemplate"]');
	}

	cartTemplates.forEach((template) => {
		const { $tpl } = template;
		const rows = lines.map((item) => {
			const $row = $tpl.clone();
			$row.removeAttr('data-rel');
			$row.attr('sapphireCartItem', 'cartEntry');
			$row.attr('sapphireEntryID', item.itemID);
			$row.find('[sapphireCartItem="name"]').html(item.itemName);
			$row.find('[sapphireCartItem="price"]').html(money(item.itemPrice));
			$row.find('[sapphireCartItem="priceSubtotal"]').html(money(item.itemPrice * item.itemQuantity));
			const $qty = $row.find('[sapphireCartItem="quantity"]');
			$qty.text(item.itemQuantity);
			$qty.val(item.itemQuantity);
			$qty.attr('value', item.itemQuantity);
			$qty.attr('name', `number-${item.itemID}`);
			$row.find('[sapphireCartItem="image"]').attr('src', imageSrc(item.itemID, item.itemImageName));
			$row.find('[sapphireCartItem="removeItemButton"]').on('click', () => {
				cart.removeItem(item.itemID);
				sapphireCartOutput();
				return false;
			});
			return $row;
		});
		if (!rows.length) {
			rows.push($('<tr class="position-relative" sapphireCartItem="cartEntry" sapphireEntryID="0"><td colspan="3" class="shop-product"><div align="center" class="p-5">Cart is empty</div></td></tr>'));
		}
		drawRows(template, rows);
	});
}

/**
 * Draws one tile per config product into every
 * [data-rel="sapphireProductsItemTemplate"]: [sapphireProductsItem="name" |
 * "price" | "image" | "addItemButton"]. The button calls the page's own
 * saphireAddToCart(<index>) and openSideCart(), as on demo-engine.
 */
export function sapphireDisplayProducts() {
	const products = getConfig().products || [];

	if (!productTemplates) {
		productTemplates = takeTemplates('[data-rel="sapphireProductsItemTemplate"]');
	}

	productTemplates.forEach((template) => {
		const { $tpl } = template;
		const tiles = products.map((product, n) => {
			const $tile = $tpl.clone();
			$tile.attr('sapphireProductsItem', 'prodEntry');
			$tile.attr('sapphireEntryID', product.pid_konnektive);
			$tile.find('[sapphireProductsItem="name"]').html(product.name);
			$tile.find('[sapphireProductsItem="price"]').html(money(product.price));
			$tile.find('[sapphireProductsItem="image"]').attr('src', imageSrc(product.pid_konnektive, product.imageName));
			$tile.find('[sapphireProductsItem="addItemButton"]')
				.attr('onclick', `javascript: saphireAddToCart(${n}); openSideCart(); return false;`);
			return $tile;
		});
		if (!tiles.length) {
			tiles.push($('<div class="col-lg-3 col-md-4 col-sm-6 product-entry" data-rel="sapphireProductsItemTemplate" sapphireProductsItem="prodEntry"><div align="center" class="p-5">Products are empty</div></div>'));
		}
		drawRows(template, tiles);
	});
}

// The product-page "Add to cart" button: [data-rel="sapphireAddToCartButton"]
// with sapphireCartProductIndex="<config product index>", quantity from
// [data-rel="sapphireProductQuantity"]. As demo-engine's render() bound it.
export function bindSapphireAddToCartButton() {
	$('[data-rel="sapphireAddToCartButton"]').on('click', function onAdd() {
		const index = $(this).attr('sapphireCartProductIndex');
		const product = (getConfig().products || [])[index];
		if (!product) return;
		const qty = parseInt($('[data-rel="sapphireProductQuantity"]').val(), 10) || 1;
		const intl = (getConfig().international || [])[0] || {};
		const ship = intl.US && intl.US[0] ? intl.US[0].shipping : 0;
		const cart = getSapphireCart();
		cart.loadCart();
		cart.addItem(
			product.name,
			product.price,
			ship,
			qty,
			product.pid_konnektive,
			product.campaignID_konnektive,
			0,
			0,
			0,
			product.imageName || '',
			'',
		);
		sapphireCartOutput();
	});
}
